import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "../api";
import { useAuth } from "../AuthContext";
import { Spinner, StatusPill, relTime } from "../ui";

function CaseCard({ kase }) {
  return (
    <Link
      to={`/app/case/${kase.id}`}
      data-testid={`case-card-${kase.public_case_number}`}
      className="group block bg-[#26241F] border border-white/[0.08] rounded-2xl p-6 hover:border-[#B5A58A]/40 transition-colors duration-300"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="font-mono-label text-[9px] tracking-[0.25em] text-[#B5A58A]/80">{kase.public_case_number}</p>
        <ArrowUpRight className="w-4 h-4 text-stone-500 group-hover:text-[#B5A58A] transition-colors duration-300" strokeWidth={1.5} />
      </div>
      <h3 className="font-serif-display font-light tracking-tight text-2xl text-white mb-3">{kase.title}</h3>
      <div className="flex items-center gap-3">
        <StatusPill status={kase.status} />
        <span className="font-mono-label text-[9px] tracking-[0.15em] uppercase text-stone-500">{relTime(kase.last_updated_at)}</span>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    api.get("/cases").then((r) => setData(r.data)).catch(() => setData({ cases: [] }));
    if (!user?.is_guest) {
      api.get("/decision-profile").then((r) => setInsights(r.data.insights || r.data || [])).catch(() => {});
    }
  }, [user]);

  if (!data) return <Spinner label="Opening your cases…" />;

  const cases = data.cases || [];
  const active = cases.filter((c) => !["RESOLVED", "ARCHIVED"].includes(c.status)).slice(0, 5);
  const resolved = cases.filter((c) => c.status === "RESOLVED").slice(0, 3);
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div data-testid="home-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
        <p className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-[#B5A58A]/80 mb-4">
          {greet}, {user?.is_guest ? "Guest" : user?.name || "Member"}
        </p>
        <h1 className="font-serif-display font-light tracking-tighter leading-[0.95] text-5xl md:text-7xl text-white mb-10">
          What are you <span className="italic">dealing with?</span>
        </h1>
        <Link
          to="/app/new"
          data-testid="home-open-case-cta"
          className="group inline-flex items-center gap-4 bg-[#B5A58A] text-[#1F1E1B] rounded-full pl-8 pr-4 py-4 hover:bg-[#c4b59a] transition-colors duration-300"
        >
          <span className="font-mono-label text-[11px] tracking-[0.25em] uppercase">Open a new case</span>
          <span className="w-10 h-10 rounded-full bg-[#1F1E1B]/10 flex items-center justify-center">
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-300" strokeWidth={1.5} />
          </span>
        </Link>
      </motion.div>

      <section className="mt-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-stone-500">Active cases</h2>
          <Link to="/app/cases" data-testid="home-all-cases" className="font-mono-label text-[9px] tracking-[0.2em] uppercase text-stone-400 hover:text-white transition-colors duration-300">
            View all
          </Link>
        </div>
        {active.length === 0 ? (
          <p className="text-sm text-stone-500 leading-relaxed" data-testid="home-no-cases">
            No active cases. When a decision keeps you up at night, open one — the board is seated.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {active.map((c) => <CaseCard key={c.id} kase={c} />)}
          </div>
        )}
      </section>

      {resolved.length > 0 && (
        <section className="mt-14">
          <h2 className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-6">Recently resolved</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {resolved.map((c) => <CaseCard key={c.id} kase={c} />)}
          </div>
        </section>
      )}

      {insights.length > 0 && (
        <section className="mt-14">
          <h2 className="font-mono-label text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-6">Decision profile</h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {insights.slice(0, 5).map((ins, i) => (
              <div key={ins.id || i} className="min-w-[280px] bg-[#26241F] border border-white/[0.08] rounded-2xl p-5">
                <p className="text-sm leading-relaxed text-stone-300">{ins.content || ins.text || ins.insight}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
